import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '@diggens/api-client';

interface UseAppVersionReturn {
  updateAvailable: boolean;
  deployedAt: string | null;
  buildId: string;
  refresh: () => void;
  dismiss: () => void;
}

const BUILD_ID = typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_APP_VERSION ? (import.meta as any).env.VITE_APP_VERSION : 'local-dev';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function useAppVersion(app: 'web' | 'crm'): UseAppVersionReturn {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [deployedAt, setDeployedAt] = useState<string | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      const result = await apiFetch<{ version: string; deployedAt?: string }>(`/version?app=${app}`);
      if (result.version && result.version !== BUILD_ID && BUILD_ID !== 'local-dev') {
        setDeployedAt(result.deployedAt ?? null);
        setUpdateAvailable(true);
      }
    } catch {
      // silently ignore version check failures
    }
  }, [app]);

  useEffect(() => {
    checkVersion();
    const interval = setInterval(checkVersion, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkVersion]);

  const refresh = () => {
    window.location.reload();
  };

  const dismiss = () => {
    setUpdateAvailable(false);
  };

  return {
    updateAvailable,
    deployedAt,
    buildId: BUILD_ID,
    refresh,
    dismiss,
  };
}
